import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportVersionEditStore } from '@stores/dictionaryStores/reportVersion/reportVersionEditStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../FormWithActions';
import { ReportVersionForm } from './ReportVersionForm';
import { validate } from './validateSchema';

const ReportVersionEditComponent = () => {
  const { code } = useParams<{ code: string }>();
  const { push } = useHistory();
  const { setNew, fetch, setEditMode, isLoading, reportVersion, save, isNew, isEditMode } = reportVersionEditStore;

  const permission = usePermission('CmReportVersion', reportVersionEditStore);

  useEffect(() => {
    fetchReferences(['reports']);
  }, []);

  useEffect(() => {
    if (code !== NEW_ENTITY_ID) {
      fetch(code);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, code, setNew, setEditMode]);

  const initialValues = useMemo(
    () => (isLoading ? {} : toJS({ ...reportVersion, id: reportVersion?.code } || {})), // id нужен для проверки на уникальность кода
    [reportVersion, isLoading],
  );

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);
      if (validation) {
        return validation;
      }

      (async () => {
        const reportVersionCode = await save({
          ...values,
          beginDate: values.beginDate ? dayjs(new Date(values.beginDate)).format('YYYY-MM-DD') : null,
          finishDate: values.finishDate ? dayjs(new Date(values.finishDate)).format('YYYY-MM-DD') : null,
        });
        if (isNew && reportVersionCode) {
          push(`${RoutePath.DICTIONARY_REPORT_VERSION}/${reportVersionCode}`);
        }
      })();

      return undefined;
    },
    [permission, isNew, push, save],
  );

  const goToReportVersions = useCallback(() => push(RoutePath.DICTIONARY_REPORT_VERSION), [push]);

  return (
    <DesignLayout title="Справочник: Версии отчетов" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper initialValues={initialValues} onSubmit={handleSubmit} layout="vertical" compact>
        <FormWithActions store={reportVersionEditStore} goBackLink={goToReportVersions}>
          <ReportVersionForm />
        </FormWithActions>
      </FormWrapper>
    </DesignLayout>
  );
};

export const ReportVersionEdit = observer(ReportVersionEditComponent);
